import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";

const STORAGE_KEY = "screenshot-mode-enabled";

interface ScreenshotModeContextValue {
  enabled: boolean;
  setEnabled: (v: boolean) => void;
  toggle: () => void;
}

const ScreenshotModeContext = createContext<ScreenshotModeContextValue | undefined>(undefined);

function readInitial(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem(STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

export const ScreenshotModeProvider = ({ children }: { children: ReactNode }) => {
  const [enabled, setEnabledState] = useState<boolean>(readInitial);

  const setEnabled = useCallback((v: boolean) => {
    setEnabledState(v);
    try {
      if (v) window.localStorage.setItem(STORAGE_KEY, "1");
      else window.localStorage.removeItem(STORAGE_KEY);
    } catch { /* ignore */ }
  }, []);

  const toggle = useCallback(() => setEnabled(!enabled), [enabled, setEnabled]);

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key !== STORAGE_KEY) return;
      setEnabledState(e.newValue === "1");
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const value = useMemo<ScreenshotModeContextValue>(
    () => ({ enabled, setEnabled, toggle }),
    [enabled, setEnabled, toggle],
  );

  return <ScreenshotModeContext.Provider value={value}>{children}</ScreenshotModeContext.Provider>;
};

export function useScreenshotMode(): ScreenshotModeContextValue {
  const ctx = useContext(ScreenshotModeContext);
  if (!ctx) return { enabled: false, setEnabled: () => {}, toggle: () => {} };
  return ctx;
}